import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type Auth,
  type ConfirmationResult,
} from "firebase/auth";

export type { ConfirmationResult };

const metaEnv = (import.meta as unknown as { env?: Record<string, string> }).env;

// Production Trado Firebase Public Config (my-daily-direct)
const firebaseConfig = {
  apiKey: metaEnv?.VITE_FIREBASE_API_KEY || "AIzaSyBr-3-0aXJ7wgEi6aY7xfnjTSRswqnNmoc",
  authDomain: metaEnv?.VITE_FIREBASE_AUTH_DOMAIN || "my-daily-direct.firebaseapp.com",
  projectId: metaEnv?.VITE_FIREBASE_PROJECT_ID || "my-daily-direct",
  storageBucket: metaEnv?.VITE_FIREBASE_STORAGE_BUCKET || "my-daily-direct.firebasestorage.app",
  messagingSenderId: metaEnv?.VITE_FIREBASE_MESSAGING_SENDER_ID || "324267693611",
  appId: metaEnv?.VITE_FIREBASE_APP_ID || "1:324267693611:web:5c6cf1c9624789ad0a7c6e",
  measurementId: metaEnv?.VITE_FIREBASE_MEASUREMENT_ID || "G-56HC7MP1JK",
};

export function getFirebaseApp(): FirebaseApp {
  const currentApps = getApps();
  if (currentApps.length > 0) {
    return currentApps[0];
  }
  return initializeApp(firebaseConfig);
}

export function getFirebaseAuth(): Auth {
  const app = getFirebaseApp();
  return getAuth(app);
}

/**
 * Sends a phone verification OTP to the user's mobile number.
 * Supports test phone numbers (e.g. +91 9876543210 with test code 123456).
 */
export async function sendFirebasePhoneOTP(
  phoneNumber: string,
  containerIdOrElement?: string | HTMLElement,
): Promise<{ confirmationResult: ConfirmationResult | null; isDemoMode: boolean }> {
  // Format phone number to E.164 (e.g. +919876543210)
  const formattedPhone = formatToE164(phoneNumber);

  try {
    const auth = getFirebaseAuth();

    // Clean up existing recaptcha verifier if re-submitting
    if ((window as any).recaptchaVerifier) {
      try {
        (window as any).recaptchaVerifier.clear();
      } catch (ignored) {}
    }

    const verifier = new RecaptchaVerifier(auth, containerIdOrElement || "recaptcha-container", {
      size: "invisible",
    });
    (window as any).recaptchaVerifier = verifier;

    const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, verifier);
    return { confirmationResult, isDemoMode: false };
  } catch (error: any) {
    console.error("Firebase Phone Auth error:", error);
    const code = error?.code || "";
    if (code === "auth/operation-not-allowed") {
      throw new Error("Phone Authentication is not enabled in Firebase Console. Please enable Phone sign-in method in Firebase Console -> Authentication.");
    }
    if (code === "auth/unauthorized-domain") {
      throw new Error("This domain is not authorized in Firebase Console. Please add this domain to Firebase Console -> Authentication -> Settings -> Authorized domains.");
    }
    if (code === "auth/invalid-phone-number") {
      throw new Error("Invalid phone number format. Please enter a valid 10-digit mobile number.");
    }
    throw new Error(error?.message || "Failed to send SMS OTP. Please check your phone number and try again.");
  }
}

/**
 * Verifies a 6-digit OTP code against Firebase confirmation result or local demo engine.
 */
export async function verifyFirebasePhoneOTP(
  confirmationResult: ConfirmationResult | null,
  otpCode: string,
): Promise<boolean> {
  const cleanCode = otpCode.trim();

  // No Firebase confirmation means no OTP was actually sent — never accept a code.
  if (!confirmationResult) {
    return false;
  }

  try {
    const userCredential = await confirmationResult.confirm(cleanCode);
    return Boolean(userCredential.user);
  } catch (error) {
    throw new Error("Invalid verification code. Please check the 6-digit OTP and try again.");
  }
}

function formatToE164(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+91${digits}`;
  }
  if (digits.startsWith("91") && digits.length === 12) {
    return `+${digits}`;
  }
  return phone.startsWith("+") ? phone : `+${digits}`;
}
